/**
 * DepartmentGeofences — per-department geofence overrides.
 *
 * Migrated to live API in Wave 3.2. Sibling to WorkerGeofences; both
 * use the same /v1/attendance/geofences table with scope differentiated
 * by which FK column is non-null (department_id vs worker_id vs both-null).
 * Workers without an individual override fall back to their
 * department's geofence; departments without an override fall back to
 * the org default (e.g. the HQ pin seeded by seed_multi_orgs.py).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, IconButton,
  InputLabel, MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, ApartmentOutlined, DeleteOutlined, EditOutlined, RefreshOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type { Geofence, GeofenceCreate } from '../../../api/attendance';
import type { Schema } from '../../../api/types';
import { ConfirmDialog } from '../../../components/primitives';
import GeofenceMapDialog, { GeofenceFormData } from './GeofenceMapDialog';
import GeofenceScopePriorityTip from './GeofenceScopePriorityTip';
import GeofenceRulesHint from './GeofenceRulesHint';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';

type Department = Schema<'DepartmentResponse'>;

const DepartmentGeofences: React.FC = () => {
  const { t } = useTranslation();
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Geofence | null>(null);
  const [departmentId, setDepartmentId] = useState('');
  const [geofenceName, setGeofenceName] = useState('');
  const [initialMapData, setInitialMapData] = useState<GeofenceFormData | undefined>();
  const [pendingDelete, setPendingDelete] = useState<Geofence | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [gs, ds] = await Promise.all([
        attendanceApi.geofencesApi.list('department'),
        organizationApi.departmentsApi.list(),
      ]);
      setGeofences(gs.filter(g => g.department_id));
      setDepartments(ds);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:deptGeofences.loadFailed'));
      setGeofences([]);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deptById = useMemo(() => {
    const m = new Map<string, Department>();
    for (const d of departments) m.set(d.id, d);
    return m;
  }, [departments]);

  const rows = useMemo(
    () => geofences.map(g => ({ g, deptName: deptById.get(g.department_id ?? '')?.name ?? '—' })),
    [geofences, deptById],
  );
  const filtered = useMemo(
    () => rows.filter(r =>
      r.deptName.toLowerCase().includes(filter.toLowerCase()) ||
      r.g.name.toLowerCase().includes(filter.toLowerCase()),
    ),
    [rows, filter],
  );
  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleOpen = (g?: Geofence) => {
    if (g) {
      setEditing(g);
      setDepartmentId(g.department_id ?? '');
      setGeofenceName(g.name);
      setInitialMapData({
        address: g.address ?? '',
        radius: g.radius_meters,
        lat: Number(g.lat),
        lng: Number(g.lng),
      });
    } else {
      setEditing(null);
      setDepartmentId(departments[0]?.id ?? '');
      setGeofenceName('');
      setInitialMapData(undefined);
    }
    setDialogOpen(true);
  };

  const handleSave = async (map: GeofenceFormData) => {
    if (!departmentId || !geofenceName.trim()) return;
    const payload: GeofenceCreate = {
      name: geofenceName,
      department_id: departmentId,
      lat: map.lat,
      lng: map.lng,
      radius_meters: map.radius,
      address: map.address,
    };
    try {
      if (editing) {
        await attendanceApi.geofencesApi.update(editing.id, payload);
        setToast(t('attendance:deptGeofences.geofenceUpdated'));
      } else {
        await attendanceApi.geofencesApi.create(payload);
        setToast(t('attendance:deptGeofences.geofenceAdded'));
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:deptGeofences.saveFailed'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await attendanceApi.geofencesApi.delete(pendingDelete.id);
      setToast(t('attendance:deptGeofences.geofenceDeleted'));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:deptGeofences.deleteFailed'));
      throw e;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:deptGeofences.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:deptGeofences.subtitleDetail')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpen()} disabled={departments.length === 0}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('attendance:deptGeofences.addGeofence')}</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      <GeofenceScopePriorityTip current="department" />

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" label={t('attendance:deptGeofences.filterLabel', { defaultValue: 'Filter by department or name' })} value={filter}
          onChange={e => { setFilter(e.target.value); setPage(0); }}
          sx={{ width: 400, maxWidth: '100%', mr: 2 }} />
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]} />
      </Box>

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('attendance:timeEntries.departmentLabel')}</TableCell>
                <TableCell>{t('attendance:workerGeofences.colName', { defaultValue: 'Geofence name' })}</TableCell>
                <TableCell>{t('attendance:workerGeofences.colRadius', { defaultValue: 'Radius (m)' })}</TableCell>
                <TableCell>{t('attendance:workerGeofences.colAddress', { defaultValue: 'Address' })}</TableCell>
                <TableCell align="center">{t('attendance:deptGeofences.colEnabled')}</TableCell>
                <TableCell align="center">{t('attendance:deptGeofences.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map(({ g, deptName }) => (
                <TableRow key={g.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ApartmentOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{deptName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{g.name}</TableCell>
                  <TableCell>{g.radius_meters}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{g.address ?? '—'}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip label={g.enabled ? t('common:answers.yes') : t('common:answers.no')} size="small"
                      sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem', bgcolor: g.enabled ? 'success.light' : 'grey.200', color: g.enabled ? 'success.dark' : 'text.secondary' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton size="small" onClick={() => handleOpen(g)}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>
                      <IconButton size="small" onClick={() => setPendingDelete(g)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {paginated.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">{filter ? t('attendance:deptGeofences.noMatches') : t('attendance:deptGeofences.noGeofences')}</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        {t('attendance:deptGeofences.fallbackTip')}{' '}
        <Typography
          component="span"
          variant="caption"
          sx={{
            color: 'primary.main',
            cursor: 'pointer',
            fontWeight: 600,
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => (window.location.href = '/organization/departments')}
        >
          {t('attendance:deptGeofences.manageDepartments')}
        </Typography>
      </Typography>

      <GeofenceMapDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        title={editing ? t('attendance:deptGeofences.editGeofence') : t('attendance:deptGeofences.addDeptGeofence')}
        subtitle={t('attendance:deptGeofences.mapSubtitle')}
        initialData={initialMapData}
        saveDisabled={!departmentId || !geofenceName.trim()}
        saveLabel={editing ? t('attendance:deptGeofences.saveChanges') : t('attendance:deptGeofences.addGeofence')}
        extraFields={
          <>
            <FormControl size="small" fullWidth disabled={!!editing}>
              <InputLabel>{t('attendance:deptGeofences.departmentLabel')}</InputLabel>
              <Select value={departmentId} label={t('attendance:deptGeofences.departmentLabel')} onChange={e => setDepartmentId(e.target.value)}>
                {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label={t('attendance:deptGeofences.geofenceNameLabel')} value={geofenceName} onChange={e => setGeofenceName(e.target.value)} size="small" fullWidth placeholder={t('attendance:deptGeofences.geofenceNamePlaceholder')} />
          </>
        }
      />

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('attendance:deptGeofences.deleteTitle')}
        description={t('attendance:deptGeofences.deleteDescription')}
        subject={pendingDelete && <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{pendingDelete.name}</Box>}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
      <GeofenceRulesHint />
    </Box>
  );
};

export default DepartmentGeofences;
